// cart-drawer.js

// Function to check if URL has UTM parameters
function hasUtmParameters() {
  return Array.from(new URLSearchParams(window.location.search).keys()).some(param => param.toLowerCase().startsWith('utm_'));
}

// Function to update cart icon bubble
function updateCartIcon() {
  fetch(window.Shopify.routes.root + '?section_id=cart-icon-bubble')
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const newIcon = html.querySelector('#cart-icon-bubble');
      const existingIcon = document.querySelector('#cart-icon-bubble');
      if (newIcon && existingIcon) {
        existingIcon.innerHTML = newIcon.innerHTML;
        // Re-attach click handler after innerHTML update
        bindCartIconClickHandler(existingIcon);
      }
    })
    .catch((e) => console.error('Error updating cart icon:', e));
}

// Bind cart icon click handler (prevents /cart navigation, opens drawer instead)
function bindCartIconClickHandler(cartIcon) {
  if (!cartIcon) return;
  
  // Remove any existing handler to prevent duplicates
  cartIcon.removeEventListener('click', cartIconClickHandler);
  cartIcon.addEventListener('click', cartIconClickHandler);
}

function cartIconClickHandler(event) {
  event.preventDefault();
  event.stopPropagation();
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open(event.currentTarget);
  }
}

// Track if add-to-cart button was clicked
let addToCartClicked = false;

// Track if drawer was recently opened to prevent rapid open/close
let drawerRecentlyOpened = false;

// Listen for add-to-cart button clicks
document.addEventListener('click', function(e) {
  if (e.target.matches('[type="submit"]') && e.target.closest('form[action*="/cart/add"]')) {
    addToCartClicked = true;
    setTimeout(() => {
      addToCartClicked = false;
    }, 3000);
  }
});

// Single function to open cart drawer (prevents multiple rapid opens)
function openCartDrawerOnce() {
  if (drawerRecentlyOpened) {
    console.log('[Cart Drawer] Skipping open - already opened recently');
    return;
  }
  
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    drawerRecentlyOpened = true;
    cartDrawer.open();
    
    // Reset flag after a delay
    setTimeout(() => {
      drawerRecentlyOpened = false;
    }, 1000);
  }
}

// Function to refresh the cart drawer after an AJAX add-to-cart
function refreshCartDrawer() {
  fetch(window.Shopify.routes.root + "?section_id=cart-drawer")
    .then((response) => response.text())
    .then((responseText) => {
      const parser = new DOMParser();
      const html = parser.parseFromString(responseText, "text/html");

      // Replace cart-drawer contents
      const newCartDrawer = html.querySelector("#CartDrawer");
      const existingCartDrawer = document.querySelector("#CartDrawer");
      if (newCartDrawer && existingCartDrawer) {
        existingCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }

      // Replace cart-drawer-items
      const newCartItems = html.querySelector("cart-drawer-items");
      const existingCartItems = document.querySelector("cart-drawer-items");
      if (newCartItems && existingCartItems) {
        existingCartItems.innerHTML = newCartItems.innerHTML;
      }

      // Remove "is-empty" class when items exist
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && newCartItems && newCartItems.querySelector(".drawer__cart-items-wrapper")) {
        cartDrawer.classList.remove("is-empty");
      }

      // Update cart icon
      updateCartIcon();

      // Open the cart drawer after update
      openCartDrawerOnce();
    })
    .catch((e) => console.error("Cart update error:", e));
}

// Listen for cart:updated events
document.addEventListener("cart:updated", (event) => {
  console.log("CART UPDATED EVENT", event.detail);
  refreshCartDrawer();
});

// Cart Drawer Component
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;
    
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    
    // Use the global click handler
    bindCartIconClickHandler(cartLink);
    
    // Keyboard accessibility
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Check UTM parameters
    if (hasUtmParameters() && !triggeredBy && !addToCartClicked) {
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove("overflow-hidden");
  }

  renderContents(parsedState) {
    const cartDrawer = this.querySelector("cart-drawer");

    if (cartDrawer && parsedState && parsedState.item_count > 0) {
      cartDrawer.classList.remove("is-empty");
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement && parsedState && parsedState.sections && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        overlay.addEventListener("click", this.close.bind(this));
      }
      if (!hasUtmParameters() || addToCartClicked) {
        openCartDrawerOnce();
      }
    });

    updateCartIcon();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" }
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" }
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Ensure cart icon click handler is bound on page load
document.addEventListener('DOMContentLoaded', function() {
  const cartIcon = document.querySelector('#cart-icon-bubble');
  if (cartIcon) {
    bindCartIconClickHandler(cartIcon);
  }
});

// Also bind immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  const cartIcon = document.querySelector('#cart-icon-bubble');
  if (cartIcon) {
    bindCartIconClickHandler(cartIcon);
  }
}

function buildCartAddRequestBodyFromForm(form) {
  const formData = new FormData(form);

  const variantId = formData.get('id');
  const quantity = formData.get('quantity') || 1;
  const sellingPlan = formData.get('selling_plan');

  // Collect Shopify line item properties: inputs named like properties[...]
  // PLUS a safety net for apps that submit _givy_* directly.
  const properties = {};

  for (const [key, value] of formData.entries()) {
    const v = String(value ?? '').trim();
    if (!v) continue;

    const match = key.match(/^properties\[(.+)\]$/);
    if (match) {
      properties[match[1]] = v;
      continue;
    }

    // Safety net: if the app posts _givy_* fields not wrapped in properties[]
    if (key.startsWith('_givy_') || key.startsWith('givy_')) {
      properties[key] = v;
    }
  }

  // Parse values
  const parsedVariantId = parseInt(String(variantId).trim(), 10);
  const parsedQuantity = parseInt(String(quantity).trim(), 10) || 1;

  if (!parsedVariantId || Number.isNaN(parsedVariantId)) {
    return null;
  }

  const requestBody = {
    id: parsedVariantId,
    quantity: parsedQuantity
  };

  // Include selling_plan if present (subscriptions)
  if (sellingPlan && String(sellingPlan).trim()) {
    const parsedSellingPlan = parseInt(String(sellingPlan).trim(), 10);
    if (!Number.isNaN(parsedSellingPlan)) requestBody.selling_plan = parsedSellingPlan;
  }

  // ✅ The important part for Givy: pass properties through
  if (Object.keys(properties).length) {
    requestBody.properties = properties;
  }

  return requestBody;
}

// =============================================================
// UNIVERSAL ADD-TO-CART HANDLER
// Intercepts ALL forms with data-type="add-to-cart-form" or
// action="/cart/add" - PREVENTS /cart redirect, uses AJAX instead
// =============================================================

// Track forms we're already processing to prevent duplicates
const processingForms = new WeakSet();

function universalAddToCart(form, submitButton) {
  // Prevent double submissions
  if (processingForms.has(form)) {
    console.log('[Universal Add-to-Cart] Already processing this form, skipping');
    return;
  }

  processingForms.add(form);

  // Show loading state
  if (submitButton) {
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.disabled = true;
    submitButton.classList.add('loading');
  }

  const spinner = form.querySelector('.loading__spinner') || (submitButton && submitButton.querySelector('.loading__spinner'));
  if (spinner) spinner.classList.remove('hidden');


  // Debug: Log form data
  const formData = new FormData(form);
  console.log('[Universal Add-to-Cart] Form data:');
  for (let [key, value] of formData.entries()) {
    console.log(`  ${key}: ${value}`);
  }

  // ✅ Build request body from form (includes properties[...] like Givy)
  const requestBody = buildCartAddRequestBodyFromForm(form);

  if (!requestBody) {
    console.error('[Universal Add-to-Cart] Invalid / missing variant ID in form.');
    const idInput = form.querySelector('input[name="id"]');
    console.error('[Universal Add-to-Cart] Direct input check - id input:', idInput, 'value:', idInput?.value);

    processingForms.delete(form);
    if (submitButton) {
      submitButton.removeAttribute('aria-disabled');
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
    }

    // Restore disabled inputs
    form.querySelectorAll('[data-was-disabled="true"]').forEach((el) => {
      el.disabled = true;
      delete el.dataset.wasDisabled;
    });

    if (spinner) spinner.classList.add('hidden');
    return;
  }

  console.log('[Universal Add-to-Cart] Request body:', JSON.stringify(requestBody));

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(requestBody)
  })
    .then((response) => {
      console.log('[Universal Add-to-Cart] Response status:', response.status);
      return response.json();
    })
    .then((data) => {
      console.log('[Universal Add-to-Cart] Response data:', data);
      if (data.status === 422 || data.status === 'error' || data.message) {
        const errorMsg = data.description || data.message || 'Could not add to cart';
        console.error('[Universal Add-to-Cart] Shopify Error:', errorMsg, 'Full response:', data);
        alert(errorMsg);
      } else {
        console.log('[Universal Add-to-Cart] Success:', data);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }
    })
    .catch((error) => {
      console.error('[Universal Add-to-Cart] Network error:', error);
    })
    .finally(() => {
      processingForms.delete(form);

      if (submitButton) {
        submitButton.removeAttribute('aria-disabled');
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
      }

      if (spinner) spinner.classList.add('hidden');

      // Restore inputs that were disabled
      form.querySelectorAll('[data-was-disabled="true"]').forEach((el) => {
        el.disabled = true;
        delete el.dataset.wasDisabled;
      });
    });
}

// Check if form is an add-to-cart form
function isAddToCartForm(form) {
  if (!form || form.tagName !== 'FORM') return false;
  return form.getAttribute('data-type') === 'add-to-cart-form' ||
         form.dataset.ajaxHijacked === 'true' ||
         (form.dataset.originalAction && form.dataset.originalAction.includes('/cart/add')) ||
         (form.action && form.action.includes('/cart/add'));
}

// Hijack ALL add-to-cart forms on page load and dynamically
function hijackAddToCartForms() {
  // Get ALL add-to-cart forms, including those inside product-form
  const forms = document.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
  
  forms.forEach(form => {
    // Mark as hijacked to prevent double-handling
    if (form.dataset.ajaxHijacked) return;
    form.dataset.ajaxHijacked = 'true';
    
    // Remove the action to prevent native form submission (this is the key fix!)
    const originalAction = form.getAttribute('action');
    form.removeAttribute('action');
    form.dataset.originalAction = originalAction || '/cart/add';
    
    // Enable any disabled variant inputs (needed for submission)
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && variantInput.disabled) {
      variantInput.disabled = false;
    }
    
    // Add our submit handler with highest priority
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      const submitButton = form.querySelector('button[type="submit"]') || 
                          form.querySelector('[type="submit"]');
      universalAddToCart(form, submitButton);
      return false;
    }, true); // Capture phase
    
    console.log('[Universal Add-to-Cart] Hijacked form:', form.id || form.className);
  });
}

// Run on DOMContentLoaded
document.addEventListener('DOMContentLoaded', hijackAddToCartForms);

// Also run immediately if DOM already loaded
if (document.readyState !== 'loading') {
  hijackAddToCartForms();
}

// Watch for dynamically added forms
const formObserver = new MutationObserver(function(mutations) {
  let shouldCheck = false;
  mutations.forEach(function(mutation) {
    if (mutation.addedNodes.length) {
      shouldCheck = true;
    }
  });
  if (shouldCheck) {
    hijackAddToCartForms();
  }
});

formObserver.observe(document.body || document.documentElement, {
  childList: true,
  subtree: true
});

// BACKUP: Capture phase handlers - intercepts ALL add-to-cart forms
document.addEventListener('submit', function(e) {
  const form = e.target;
  if (!isAddToCartForm(form)) return;
  
  // Already being handled by our hijack
  if (form.dataset.ajaxHijacked) return;
  
  console.log('[Universal Add-to-Cart] Capture phase - intercepting submit');
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  
  // Enable variant input if disabled
  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput && variantInput.disabled) {
    variantInput.disabled = false;
  }
  
  const submitButton = form.querySelector('button[type="submit"]') || 
                      form.querySelector('[type="submit"]');
  universalAddToCart(form, submitButton);
  return false;
}, true);

document.addEventListener('click', function(e) {
  const button = e.target.closest('button[type="submit"], input[type="submit"], [type="submit"]');
  if (!button) return;
  
  const form = button.closest('form');
  if (!form || !isAddToCartForm(form)) return;
  
  // Already being handled by our hijack
  if (form.dataset.ajaxHijacked) return;
  
  console.log('[Universal Add-to-Cart] Capture phase - intercepting click');
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  
  // Enable variant input if disabled
  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput && variantInput.disabled) {
    variantInput.disabled = false;
  }
  
  universalAddToCart(form, button);
  return false;
}, true);
